import * as React from 'react';
import classNames from 'classnames';
import { anyPanelsDrawn } from '__editor/googleMapsRoofsSelector/components/drawingManager/drawingManagerHelper';
import { connect } from 'react-redux';
import { CLEAR_BOM_PK, CLEAR_PK, DONT_GO_TO_ENGINEERING_FROM_BOM, GO_TO_ENGINEERING_FROM_BOM } from 'actions';
import { panelsEditorEnabled, deactivePanelsEditor } from '__editor/panelsEditor/panelsEditorHelper';
import { saveBom } from 'bom/utils/saveBom';
import {
  saveProject,
  savePanelEditorAndSaveProject,
  validatePanelQtyFromPermitForm,
} from '__editor/components/roofsSelector/components/roofsSelectorSaveLoadProject/saveProject';
import { state as globalState } from '__common/store';
import { useLocation, useNavigate } from 'react-router-dom';

type Props = {
  dispatch: Function,
  projectId?: string,
  productId?: string,
  drawingManager: drawingManagerState,
  railsSelector: railsSelectorState,
  moduleSelector: moduleSelectorState,
  panels: panelsState,
  projectConfiguration: projectConfigurationState,
  settings: settingsState,
};

function NavigationBar(props: Props) {
  const {
    productId,
    projectId,
    drawingManager,
    projectConfiguration,
    moduleSelector,
    settings,
    dispatch,
  } = props;

  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  /* ---------------- ENV HELPERS ---------------- */

  const isHelioscope = React.useCallback(() => pathname.includes('/helioscope/'), [pathname]);

  const isAurora = React.useCallback(() => {
    const { projectConfiguration: { projectEnvConfig } } = globalState();
    return projectEnvConfig?.aurora_project_id;
  }, []);

  const isOs = React.useCallback(() => {
    const { projectConfiguration: { projectEnvConfig } } = globalState();
    return projectEnvConfig?.os_project_id;
  }, []);

  /* ---------------- TAB STATE HELPERS ---------------- */

  const enableProjectInfoTab = React.useCallback(() => {
    return !(isAurora() || isOs()) &&
      (pathname.includes('project/design') || isHelioscope());
  }, [isAurora, isOs, isHelioscope, pathname]);

  const enableBomTab = React.useCallback(() => {
    return pathname.includes('project/bom') &&
      !(isAurora() || isOs());
  }, [isAurora, isOs, pathname]);

  const enableEngineeringTab = React.useCallback(() => {
    return pathname.includes('project/engineering') &&
      !(isAurora() || isOs());
  }, [isAurora, isOs, pathname]);

  const inactiveTab = React.useCallback(() => {
    const { projectConfiguration: { gone_through_panel_editor, projectEnvConfig } } = globalState();
    return (
      projectEnvConfig?.aurora_project_id ||
      projectEnvConfig?.os_project_id ||
      (enableProjectInfoTab() && !anyPanelsDrawn()) ||
      (projectEnvConfig?.helioscope_id && !gone_through_panel_editor)
    );
  }, [enableProjectInfoTab]);

  const isOnHelioscopeFlyout = React.useCallback(() => {
    const { projectConfiguration: { openedDrawerPage } } = globalState();
    return openedDrawerPage === 'helioscope-selection';
  }, []);

  /* ---------------- CLASS BUILDERS ---------------- */

  const projectInfoClasses = () => {
    const { projectConfiguration: { projectEnvConfig } } = globalState();
    return classNames(
      {
        inactive: projectEnvConfig?.aurora_project_id || projectEnvConfig?.os_project_id || isOnHelioscopeFlyout(),
        active: enableProjectInfoTab() && !enableBomTab() && !enableEngineeringTab(),
        'right-border': enableEngineeringTab(),
      },
      'steps project-info-step',
    );
  };

  const bomClasses = () =>
    classNames(
      {
        'right-border': enableProjectInfoTab(),
        inactive: inactiveTab() || moduleSelector.modelDataError || isOnHelioscopeFlyout(),
        active: !enableProjectInfoTab() && enableBomTab() && !enableEngineeringTab(),
      },
      'bom-step steps',
    );

  const engineeringClasses = () =>
    classNames('engineering-step steps', {
      active: enableEngineeringTab(),
      inactive: inactiveTab() || moduleSelector.modelDataError || isOnHelioscopeFlyout(),
    });

  const headerClasses = (flag: boolean) =>
    flag ? 'header-navigation-bar two-tabs-styling' : 'header-navigation-bar';

  const hasProjectAndProduct = productId && projectId;

  const clearBomRedirectState = () => {
    dispatch(DONT_GO_TO_ENGINEERING_FROM_BOM());
    dispatch(CLEAR_BOM_PK());
    dispatch(CLEAR_PK());
  };

  // ----- handlers -----
  const onDesignClick = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (isHelioscope() || isAurora()) return;

    clearBomRedirectState();
    // Always allow navigation first - zone classification will be handled on the design page
    if (hasProjectAndProduct) navigate(`/project/design/${productId}/${projectId}`);
  };

  const onBomClick = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const roofs = drawingManager?.roofs || {};
    const projectEnvConfig = projectConfiguration?.projectEnvConfig || {};

    if (inactiveTab() || moduleSelector.modelDataError) return;
    if (!validatePanelQtyFromPermitForm(projectEnvConfig, roofs)) return;

    clearBomRedirectState();

    if (enableProjectInfoTab()) {
      // Save project first when navigating from design to BOM
      if (panelsEditorEnabled()) {
        savePanelEditorAndSaveProject();
      } else {
        saveProject(productId!, projectId!, false, true);
      }
      return;
    }

    if (hasProjectAndProduct) navigate(`/project/bom/${productId}/${projectId}`);
  };

  const onEngineeringClick = (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const roofs = drawingManager?.roofs || {};
    const projectEnvConfig = projectConfiguration?.projectEnvConfig || {};
    const modelData = moduleSelector?.modelData || {};

    if (inactiveTab() || moduleSelector.modelDataError) return;
    // Panel quantities are validated while leaving Design. Re-running that
    // Design-only validation from BOM can block navigation even though a valid
    // BOM has already been generated.
    if (!enableBomTab() && !validatePanelQtyFromPermitForm(projectEnvConfig, roofs)) return;

    if (enableBomTab()) {
      // Design flyouts were already validated before entering the BOM. Running
      // that validation here can open a design-only drawer that does not exist
      // on this page, making the Engineering tab appear to do nothing.
      // Likewise, the panel editor is not mounted on BOM, so a stale active
      // flag must not make navigation wait for its deactivation callback.
      saveBom(false);
      return;
    }

    if (enableProjectInfoTab()) {
      if (!Object.keys(modelData).length) return;

      // Same as develop: save first, remember that Engineering was requested,
      // and let the part list open Engineering after the parts have loaded.
      // From the panel editor, close it first, then continue that save.
      const openEngineeringFromDesign = () => {
        const { drawingManager: { roofs: currentRoofs } } = globalState();
        if (currentRoofs && Object.keys(currentRoofs).length) {
          dispatch(GO_TO_ENGINEERING_FROM_BOM());
          saveProject(productId!, projectId!, false);
        }
      };

      if (panelsEditorEnabled()) {
        deactivePanelsEditor(openEngineeringFromDesign);
        return;
      }

      openEngineeringFromDesign();
    }
  };

  /* ---------------- RENDER ---------------- */

  const { projectConfiguration: { projectEnvConfig: { is_aurora_project, aurora_project_id, is_os_project, os_project_id } } } = globalState();

  return (
    <div className={headerClasses(!!(aurora_project_id || is_aurora_project || is_os_project || os_project_id))}>
      <div className={projectInfoClasses()} onClick={onDesignClick} role="button" tabIndex={0}>
        <span>design</span>
      </div>
      <div className={bomClasses()} onClick={onBomClick} role="button" tabIndex={0}>
        <span>part list</span>
      </div>
      {!(aurora_project_id || is_aurora_project || is_os_project || os_project_id) && (
        <div className={engineeringClasses()} onClick={onEngineeringClick} role="button" tabIndex={0}>
          <span>engineering</span>
        </div>
      )}
    </div>
  );
}

export default connect((s: appState) => ({
  drawingManager: s.drawingManager,
  railsSelector: s.railsSelector,
  moduleSelector: s.moduleSelector,
  panels: s.panels,
  projectConfiguration: s.projectConfiguration,
    settings: s.settings,
}))(NavigationBar);
